// Full-width top bar nav for the public site. Starts transparent over the
// hero so the big center logo doesn't compete with a redundant top-left
// wordmark — the wordmark fades in once the user scrolls past the hero.
// Glass-blurred background + pan-flag hairline border appear on scroll.
// Mobile collapses to the same row, just tighter spacing — no off-center
// pill, no hamburger (only 4 links + CTA; easily fits in a scrollable row).

import { useEffect, useState } from "react";

const LINKS = [
  { id: "hero", href: "/", label: "Home" },
  { href: "/events",  label: "Events" },
  { href: "/watch",   label: "Watch Live" },
  { href: "/gallery", label: "Gallery" },
  { href: "/shop",    label: "Shop" },
  { href: "/quote",   label: "Get a Quote", primary: true },
];

// Hover tints rotate through the pan-flag palette so all three brand colors
// get a moment whenever someone brushes the nav.
const HOVER_TINTS = [
  { c: "var(--jop-pink)",           bg: "rgba(239,83,126,0.12)" },
  { c: "var(--jop-highlight-blue)", bg: "rgba(74,164,225,0.12)" },
  { c: "var(--jop-pink)",           bg: "rgba(239,83,126,0.12)" },
  { c: "var(--jop-highlight-blue)", bg: "rgba(74,164,225,0.12)" },
  { c: "var(--jop-pink)",           bg: "rgba(239,83,126,0.12)" },
];

export default function SiteNav() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 120);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goHome = (e) => {
    e.preventDefault();
    const onSiteHome = window.location.pathname === "/" || window.location.pathname === "/";
    if (onSiteHome) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.location.href = "/";
    }
  };

  return (
    <>
      <style>{`
        /* Fixed height so the bar's pixel position is stable across pages —
           regardless of which children render (wordmark visibility, CTA
           height, image-load timing). Was drifting a few px between
           /site / /events / /watch navigations. */
        .jop-nav-bar {
          position: fixed;
          top: 0; left: 0; right: 0;
          z-index: 40;
          height: 56px;
          padding: 0 1.25rem;
          display: flex;
          align-items: center;
          gap: 0.6rem;
          background: transparent;
          transition: background 0.3s ease, backdrop-filter 0.3s ease, border-color 0.3s ease;
          border-bottom: 1px solid transparent;
          font-family: var(--jop-display);
          box-sizing: border-box;
        }
        .jop-nav-bar.is-scrolled {
          background: rgba(8,12,25,0.78);
          backdrop-filter: blur(14px) saturate(140%);
          -webkit-backdrop-filter: blur(14px) saturate(140%);
        }
        .jop-nav-wrap {
          width: 100%;
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        .jop-nav-mark {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          text-decoration: none;
          color: var(--jop-cream);
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.35s ease;
          flex-shrink: 0;
        }
        .jop-nav-bar.is-scrolled .jop-nav-mark {
          opacity: 1;
          pointer-events: auto;
        }
        .jop-nav-mark img {
          height: 36px;
          width: auto;
          display: block;
          filter: drop-shadow(0 2px 10px rgba(0,0,0,0.45));
        }
        .jop-nav-spacer { flex: 1; }
        .jop-nav-links {
          display: flex;
          align-items: center;
          gap: 0.15rem;
          flex-shrink: 0;
          overflow-x: auto;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .jop-nav-links::-webkit-scrollbar { display: none; }
        .jop-nav-link {
          color: var(--jop-cream);
          text-decoration: none;
          padding: 0.45rem 0.85rem;
          border-radius: 999px;
          font-size: 0.82rem;
          font-weight: 700;
          letter-spacing: 0.04em;
          white-space: nowrap;
          transition: color 0.15s ease, background 0.15s ease, transform 0.15s ease;
        }
        .jop-nav-cta {
          color: #1a0a12;
          background: linear-gradient(180deg, #ffd154 0%, var(--jop-gold) 55%, #e69600 100%);
          text-decoration: none;
          padding: 0.55rem 1.1rem;
          border-radius: 999px;
          font-size: 0.82rem;
          font-weight: 900;
          letter-spacing: 0.04em;
          white-space: nowrap;
          box-shadow: 0 4px 14px rgba(252,179,31,0.4), 0 0 0 1px rgba(255,255,255,0.18) inset;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
          margin-left: 0.3rem;
        }
        .jop-nav-cta:hover {
          transform: translateY(-1px);
          box-shadow: 0 8px 22px rgba(252,179,31,0.55), 0 0 0 1px rgba(255,255,255,0.28) inset;
        }
        /* Pan-flag hairline — fades in when scrolled. */
        .jop-nav-bar::after {
          content: "";
          position: absolute;
          left: 0; right: 0; bottom: 0;
          height: 1px;
          background: linear-gradient(90deg,
            transparent 0%,
            rgba(239,83,126,0.55) 30%,
            rgba(252,179,31,0.65) 50%,
            rgba(74,164,225,0.55) 70%,
            transparent 100%);
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .jop-nav-bar.is-scrolled::after { opacity: 1; }
        @media (max-width: 640px) {
          .jop-nav-bar { padding: 0 0.5rem; }
          .jop-nav-link { padding: 0.4rem 0.55rem; font-size: 0.76rem; letter-spacing: 0.02em; }
          .jop-nav-cta { padding: 0.45rem 0.8rem; font-size: 0.76rem; margin-left: 0.2rem; letter-spacing: 0.02em; }
          .jop-nav-mark { display: none; }
          /* Center the whole nav on mobile — drop the flex spacer and
             set justify-content so links + CTA sit in the middle. */
          .jop-nav-spacer { display: none; }
          .jop-nav-wrap { gap: 0.25rem; justify-content: center; }
        }
      `}</style>

      <nav className={`jop-nav-bar ${scrolled ? "is-scrolled" : ""}`}>
        <div className="jop-nav-wrap">
          {/* Brand mark — fades in past the hero. Acts as a Home link. */}
          <a className="jop-nav-mark" href="/" onClick={goHome} aria-label="The Joy of Pancakes — Home">
            <img src="/jop_logo_wide.png" alt="The Joy of Pancakes" />
          </a>

          <div className="jop-nav-spacer" />

          <div className="jop-nav-links">
            {LINKS.map((l, i) => {
              const isAnchor = !!l.id;
              const tint = HOVER_TINTS[i] || HOVER_TINTS[0];
              const href = l.href || `#${l.id}`;
              if (l.primary) {
                return (
                  <a key={l.label} className="jop-nav-cta" href={href}>{l.label}</a>
                );
              }
              return (
                <a
                  key={l.label}
                  className="jop-nav-link"
                  href={href}
                  onClick={isAnchor ? (e) => {
                    e.preventDefault();
                    const onSiteHome = window.location.pathname === "/" || window.location.pathname === "/";
                    if (onSiteHome) {
                      const el = document.getElementById(l.id);
                      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                    } else {
                      window.location.href = `/${l.id === "hero" ? "" : `#${l.id}`}`;
                    }
                  } : undefined}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = tint.c;
                    e.currentTarget.style.background = tint.bg;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "var(--jop-cream)";
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  {l.label}
                </a>
              );
            })}
          </div>
        </div>
      </nav>
    </>
  );
}
